import { isNativeApp } from '../platform';

// Meta Pixel consent bridge.
//
// The bootstrap (window.fbq stub + queued fbq('init',...) + queued
// fbq('track','PageView') + conditional fbevents.js script load) lives
// inline in index.html, which runs synchronously before the React
// bundle. By the time this module loads, fbq is already a function
// pushing to its in-memory n.queue, and fbevents.js may or may not be
// on the page depending on whether the visitor was already accepted
// at first-paint.
//
// This module's only job is the React-driven side: when the
// ConsentProvider's marketing axis flips, push the canonical Meta
// consent signal (fbq('consent','grant') / 'revoke') AND make sure
// the fbevents.js script is on the page when granted. Mirrors
// gtag.js — same shape, same idempotency, same load-on-demand pattern
// for the strict-refuse → settings-modal-opt-in path.
//
// Why this module exists at all: pre-#925 the inline bootstrap also
// gated the fbq('init') and fbq('track','PageView') calls behind the
// marketing check, so the in-memory queue stayed empty for refused
// visitors. PR #925 moved those above the gate so the queue is
// populated for everyone, but without a script tag the queue never
// drains. THIS module is what drains the queue when consent flips on
// mid-session — same pattern Cookiebot / OneTrust use for tag-based
// pixels.

// Note: META_PIXEL_ID itself lives only in the inline bootstrap in
// index.html — fbq('init', ...) is queued there before this module
// loads. This module's job is purely the consent-flip side
// (grant/revoke + script injection + cookie cleanup); init is
// inherited from the page-level bootstrap via the in-memory n.queue.
// No need to duplicate the literal here.

// Clear the first-party Meta cookies. Idempotent — calling expire on
// a cookie the browser never set is a silent no-op, so this is safe
// to invoke on every revoke + on the first-paint no-consent state
// without per-call bookkeeping.
//
// Cookie inventory (as of fbevents.js circa v2.9):
//   - _fbp        : visitor id + click attribution. First-party,
//                   ~90 day TTL, set on every script load.
//   - _fbc        : last-clicked Meta ad attribution. First-party,
//                   ~90 day TTL, set only on landings from Meta ads
//                   (fbclid in URL).
// Sweeping both even when only _fbp was set is harmless — see
// clearGaCookies for the same idempotent-expire reasoning.
function clearMetaCookies() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const host = window.location.hostname;
  const parts = host.split('.');
  const apex = parts.length > 2 ? parts.slice(-2).join('.') : null;
  const expire = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${host}`;
    if (apex && !host.endsWith('.vercel.app')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${apex}`;
    }
  };
  expire('_fbp');
  expire('_fbc');
}

// Inject fbevents.js if it's not already on the page. Idempotent —
// bails the moment a tag matching the connect.facebook.net query
// already exists. Used by the dynamic-opt-in path: a visitor who
// originally refused marketing didn't get the script from the inline
// bootstrap; flipping marketing on via the settings modal calls this
// so the queued fbq('init') + fbq('track','PageView') from the
// inline bootstrap actually reach Meta.
function ensureMetaScriptInjected() {
  // Native app loads no Meta Pixel / cross-app tracker (App Store 5.1.2 / ATT).
  if (isNativeApp) return;
  if (typeof document === 'undefined') return;
  const existing = document.querySelector(
    'script[src*="connect.facebook.net/en_US/fbevents.js"]',
  );
  if (existing) return;
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(t);
}

// Push the current consent state into Meta. The fbq stub is
// bootstrapped from index.html (defined unconditionally), so
// `window.fbq` is always a function by the time this runs. The actual
// fbevents.js script may or may not be on the page — strict-refuse
// skips it — and we inject it on demand below if the user just flipped
// marketing to granted. Cookies get cleared on every revoke transition
// to satisfy Cookiebot's "no tracker cookies survive a refusal" scan.
//
// Meta's documented consent API is `fbq('consent', 'grant' | 'revoke')`
// — Cloud-side handles the rest (suppressing events after revoke,
// resuming after grant). We don't dedupe consecutive same-state calls
// because the operation is cheap and idempotent on Meta's end.
export function syncConsentToMeta({ marketing }) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }

  window.fbq('consent', marketing ? 'grant' : 'revoke');

  if (marketing) {
    ensureMetaScriptInjected();
  } else {
    clearMetaCookies();
  }
}
